import React, { useState } from 'react';
import ControlGroup from '@amzn/meridian/control-group';
import Checkbox from '@amzn/meridian/checkbox';
import {
  CoachingDetailsInput,
  ContactInput,
} from '../../../api/CoachingForm/schema';
import FieldArrayDisplay from '../LayoutComponents/FieldArrayDisplay';
import { convertReviewToDisplay } from '../../CoachingNotes/ConsolidatedContacts/Tab/Expander/ExpanderContainer';
import { IntlShape, useIntl } from 'react-intl';
import { ConsolidatedContact } from '../../../api/CoachingNotes/schema';
import Expander from '@amzn/meridian/expander';
import Row from '@amzn/meridian/row';
import styles from './checkboxGroupWithContactDetails.module.css';

type ValueType = React.ComponentProps<typeof Checkbox>['value'];

interface CheckboxGroupWithContactDetailsProps
  extends React.ComponentProps<typeof Checkbox> {
  values: ContactInput[];
  options: ConsolidatedContact[];
  details: CoachingDetailsInput[];
}

const MAX_CONTACT_REVIEW = 5;

const CheckboxGroupWithContactDetails: React.FC<
  CheckboxGroupWithContactDetailsProps
> = ({
  name,
  onChange,
  options,
  values,
  details,
}: CheckboxGroupWithContactDetailsProps): React.ReactElement => {
  const intl = useIntl();

  const filterValuesInSelection = (values: ContactInput[]) => {
    let selected: ConsolidatedContact[] = [];

    selected = options.filter(option =>
      values.find(review => review.cscContactID === option.contactID)
    );

    return selected.map(selectValue => selectValue.contactID);
  };

  const selectedValues = filterValuesInSelection(values);

  const [value, setValue] = useState<String[]>(selectedValues);

  const checkBox = options.map((option: ConsolidatedContact) => {
    const valueNotExist = value.find(v => v === option.contactID) === undefined;

    const inDetails =
      details.find(detail =>
        detail.relatedContacts.includes(option.contactID)
      ) !== undefined;

    const isDisabled =
      (value.length >= MAX_CONTACT_REVIEW && valueNotExist) || inDetails;

    return (
      <CheckBoxWithContactDetails
        contact={option}
        intl={intl}
        value={option.contactID}
        disabled={isDisabled}
      />
    );
  });

  const handleChange = (currentValue: ValueType): void => {
    setValue(currentValue);

    if (onChange) {
      onChange({ target: { name, value: currentValue } });
    }
  };

  return (
    <ControlGroup name={name} onChange={handleChange} value={value}>
      {checkBox}
    </ControlGroup>
  );
};

interface CheckBoxWithContactDetailsProps {
  contact: ConsolidatedContact;
  intl: IntlShape;
  value?: any;
  checked?: boolean;
  onChange?: (...args: any[]) => any;
  disabled?: boolean;
  name?: string;
}

const CheckBoxWithContactDetails: React.FC<CheckBoxWithContactDetailsProps> = (
  props
): React.ReactElement => {
  return (
    <Row
      spacingInset='none'
      alignmentHorizontal='justify'
      alignmentVertical='top'
    >
      <div className={styles.checkboxPadding}>
        <Checkbox {...props} />
      </div>
      <ContactDetailsExpander title={props.contact.contactID}>
        <FieldArrayDisplay
          mapKey={`checkbox_${props.contact.contactID}`}
          displayValues={convertReviewToDisplay(
            props.contact,
            props.intl,
            false
          )}
        />
      </ContactDetailsExpander>
    </Row>
  );
};

interface ExpanderProps {
  title: string;
  children: React.ReactNode;
}

const ContactDetailsExpander: React.FC<ExpanderProps> = (
  props
): React.ReactElement => {
  const [open, setOpen] = useState(false);
  return <Expander open={open} onChange={setOpen} type='list' {...props} />;
};

export default CheckboxGroupWithContactDetails;
